/**
 * Cycle date math — pure functions over the growth config so the banner,
 * team view, and admin dashboard all agree on "due", "overdue", and
 * "next check-in".
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole days from `now` until `dueDate` (negative = overdue). */
export function daysUntilDue(dueDate, now = new Date()) {
  if (!dueDate) return null;
  const due = new Date(`${dueDate}T23:59:59`);
  if (Number.isNaN(due.getTime())) return null;
  return Math.floor((due.getTime() - now.getTime()) / MS_PER_DAY);
}

export function isOverdue(dueDate, now = new Date()) {
  const days = daysUntilDue(dueDate, now);
  return days !== null && days < 0;
}

/**
 * Whether the in-app reminder should show for an employee: reminders on,
 * inside the reminder window (or overdue), and the assessment isn't done.
 */
export function shouldShowReminder({ config, status, now = new Date() }) {
  if (!config?.reminders?.inAppEnabled) return false;
  if (status === 'submitted' || status === 'reviewed') return false;
  const days = daysUntilDue(config.cycle?.assessmentDue, now);
  if (days === null) return false;
  return days <= (config.reminders.daysBefore ?? 14);
}

/** First day of the next check-in month strictly after `now`. */
export function nextCheckIn(checkInMonths, now = new Date()) {
  if (!checkInMonths?.length) return null;
  const sorted = [...checkInMonths].sort((a, b) => a - b);
  const year = now.getFullYear();
  for (const month of sorted) {
    const candidate = new Date(year, month - 1, 1);
    if (candidate > now) return candidate;
  }
  return new Date(year + 1, sorted[0] - 1, 1);
}

/** Human label like "Due in 12 days" / "Due today" / "Overdue by 3 days". */
export function dueLabel(dueDate, now = new Date()) {
  const days = daysUntilDue(dueDate, now);
  if (days === null) return '';
  if (days < 0) return `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`;
  if (days === 0) return 'Due today';
  return `Due in ${days} day${days === 1 ? '' : 's'}`;
}
